<template>
    <span v-if="shouldWrap" :class="wrapperClass" v-floating-tooltip="tippy" tabindex="0">
        <router-link v-if="to" v-slot="{ href: routeHref, navigate }" :to="to" custom>
            <a v-bind="linkProps(routeHref)" @click="(event) => handleRouterClick(event, navigate)">
                <fa v-if="busy" :icon="['fas', 'spinner']" spin size="lg" fixed-width :class="spinnerClass" />
                <span :class="contentClass">
                    <slot>
                        <t v-if="label" :path="label" />
                    </slot>
                </span>
            </a>
        </router-link>

        <a v-else-if="href" v-bind="anchorProps" @click="handleClick">
            <fa v-if="busy" :icon="['fas', 'spinner']" spin size="lg" fixed-width :class="spinnerClass" />
            <span :class="contentClass">
                <slot>
                    <t v-if="label" :path="label" />
                </slot>
            </span>
        </a>

        <button v-else v-bind="buttonProps" :type="type" @click="handleClick">
            <fa v-if="busy" :icon="['fas', 'spinner']" spin size="lg" fixed-width :class="spinnerClass" />
            <span :class="contentClass">
                <slot>
                    <t v-if="label" :path="label" />
                </slot>
            </span>
        </button>
    </span>

    <router-link v-else-if="to" v-slot="{ href: routeHref, navigate }" :to="to" custom>
        <a v-bind="linkProps(routeHref)" @click="(event) => handleRouterClick(event, navigate)">
            <fa v-if="busy" :icon="['fas', 'spinner']" spin size="lg" fixed-width :class="spinnerClass" />
            <span :class="contentClass">
                <slot>
                    <t v-if="label" :path="label" />
                </slot>
            </span>
        </a>
    </router-link>

    <a v-else-if="href" v-bind="anchorProps" @click="handleClick">
        <fa v-if="busy" :icon="['fas', 'spinner']" spin size="lg" fixed-width :class="spinnerClass" />
        <span :class="contentClass">
            <slot>
                <t v-if="label" :path="label" />
            </slot>
        </span>
    </a>

    <button v-else v-bind="buttonProps" :type="type" @click="handleClick">
        <fa v-if="busy" :icon="['fas', 'spinner']" spin size="lg" fixed-width :class="spinnerClass" />
        <span :class="contentClass">
            <slot>
                <t v-if="label" :path="label" />
            </slot>
        </span>
    </button>
</template>

<script setup lang="ts">
import type { ClassValue } from 'clsx';
import type { HTMLAttributes, Ref } from 'vue';
import { computed, inject, onBeforeMount, onBeforeUnmount, ref, useAttrs } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { cn } from '~/lib/utils';
import { translateRequiresPermissions, usePermissionsStore } from '~/plugins/permission';

defineOptions({
    inheritAttrs: false,
});

type ButtonColor = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'danger';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type ButtonType = 'button' | 'submit' | 'reset';

interface Props {
    color?: ButtonColor;
    size?: ButtonSize;
    permission?: string | string[];
    tooltip?: string;
    tooltipPlacement?: string;
    href?: string;
    to?: RouteLocationRaw;
    type?: ButtonType;
    label?: string;
    spanClass?: HTMLAttributes['class'];
    spinner?: boolean;
    showSpinner?: boolean;
    loading?: boolean;
    outline?: boolean;
    full?: boolean;
    menu?: boolean;
    square?: boolean;
    tile?: boolean;
    flushTop?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    size: 'lg',
    tooltipPlacement: 'bottom',
    type: 'button',
});

const attrs = useAttrs();
const permissions = usePermissionsStore();
const clickShowSpinner = ref(false);
const formSubmitting = ref(false);
const formCanSubmit = inject<Ref<boolean> | null>('formCanSubmit', null);
const registerFormComponent = inject<registerFormComponentFn | null>('registerFormComponent', null);

let unregisterFormButton: (() => void) | null = null;

onBeforeMount(() => {
    if (props.type !== 'submit') {
        return;
    }

    unregisterFormButton =
        registerFormComponent?.({
            onSubmit: (state) => (formSubmitting.value = state),
        }) ?? null;
});

onBeforeUnmount(() => {
    unregisterFormButton?.();
    unregisterFormButton = null;
});

const hasPerms = computed(() => (props.permission ? permissions.has(props.permission) : true));
const busy = computed(() => props.loading || props.showSpinner || clickShowSpinner.value || formSubmitting.value);
const formSubmitDisabled = computed(() => props.type === 'submit' && formCanSubmit?.value === false);

const disabled = computed(() => {
    if (attrs.disabled || attrs.disabled === '') {
        return true;
    }

    return formSubmitDisabled.value || !hasPerms.value || busy.value;
});

const hasTooltip = computed(() => props.tooltip || (props.permission && !hasPerms.value));
const shouldWrap = computed(() => disabled.value || hasTooltip.value);

const semanticColorClasses = computed(() => {
    if (!props.color) {
        return [];
    }

    const colorMap: Record<ButtonColor, string> = {
        primary: 'btn-primary',
        secondary: 'btn-secondary',
        info: 'btn-info',
        success: 'btn-success',
        warning: 'btn-warning',
        danger: 'btn-danger',
    };

    return ['btn', colorMap[props.color]];
});

const semanticSizeClasses = computed(() => (props.color ? [`btn-${props.size}`] : []));

const buttonBaseClasses =
    'inline-flex min-w-min items-center justify-center rounded-md border-2 border-solid border-transparent bg-primary-500 px-[0.5625rem] py-1.5 text-center text-sm shadow-none transition-colors hover:cursor-pointer hover:bg-primary-500/50 hover:text-primary-50/80 focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:outline-none';

const buttonColorClasses: Record<ButtonColor, string> = {
    primary: 'text-accent-500',
    secondary: 'text-primary-200 hover:text-primary-50/60',
    info: 'text-info-500',
    success: 'text-success-500',
    warning: 'text-warning-500',
    danger: 'text-danger-500',
};

const buttonSizeClasses: Record<ButtonSize, string> = {
    xs: 'px-1.5 py-[0.1875rem] text-xs',
    sm: 'px-[0.46875rem] py-[0.28125rem] text-xs',
    md: 'px-[0.5625rem] py-1.5 text-sm',
    lg: 'px-3 py-[0.46875rem] text-sm',
    xl: 'px-[0.9375rem] py-[0.5625rem] text-base',
};

const buttonUtilityClasses = computed(() => {
    if (!props.color) {
        return [];
    }

    return [
        buttonBaseClasses,
        buttonColorClasses[props.color],
        buttonSizeClasses[props.size],
        props.outline ? 'border-current bg-transparent hover:bg-primary-500/20' : null,
    ];
});

const forwardedAttrs = computed(() => {
    const { class: _class, disabled: _disabled, onClick: _onClick, ...rest } = attrs;

    return rest;
});

const wrapperClass = computed(() => cn('focus:outline-none', props.full ? 'block w-full' : null, props.spanClass as ClassValue));
const contentClass = computed(() => cn('inline-flex items-center justify-center gap-[inherit]', busy.value ? 'invisible' : null));
const spinnerClass = 'pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2';

const baseProps = computed(() => ({
    ...forwardedAttrs.value,
    class: cn(
        'relative',
        semanticColorClasses.value,
        semanticSizeClasses.value,
        props.outline ? 'btn-outline' : null,
        attrs.class as ClassValue,
        buttonUtilityClasses.value,
        props.menu ? 'w-full justify-start gap-2 rounded-sm bg-transparent px-2 py-1.5 text-left text-sm hover:bg-primary-700/60' : null,
        props.menu && props.color === 'danger' ? 'text-danger-300 hover:bg-danger-600/20 hover:text-danger-200' : null,
        props.square ? 'h-9 w-9 justify-center p-0' : null,
        props.tile ? 'h-11 w-full justify-center rounded-lg md:aspect-square md:h-auto' : null,
        props.flushTop ? 'rounded-t-none' : null,
        props.full ? 'w-full' : null,
        disabled.value ? 'disabled pointer-events-none cursor-not-allowed opacity-40' : null,
    ),
    'aria-disabled': disabled.value || undefined,
}));

const buttonProps = computed(() => ({
    ...baseProps.value,
    type: props.type,
    disabled: disabled.value,
}));

const anchorProps = computed(() => ({
    ...baseProps.value,
    href: disabled.value ? undefined : props.href,
}));

const linkProps = (href: string): Record<string, unknown> => {
    return {
        ...baseProps.value,
        href: disabled.value ? undefined : href,
    };
};

const tippy = computed(() => {
    if (props.permission && !hasPerms.value) {
        return [translateRequiresPermissions(props.permission), props.tooltipPlacement, true];
    }

    return props.tooltip ? [props.tooltip, props.tooltipPlacement] : null;
});

const callClickHandler = async (evt: MouseEvent): Promise<void> => {
    const clickHandler = attrs.onClick;

    if (Array.isArray(clickHandler)) {
        for (const handler of clickHandler) {
            if (typeof handler === 'function') {
                await handler(evt);
            }
        }

        return;
    }

    if (typeof clickHandler === 'function') {
        await clickHandler(evt);
    }
};

const runClickHandler = async (evt: MouseEvent): Promise<void> => {
    if (!attrs.onClick) {
        return;
    }

    evt.stopImmediatePropagation();

    if (props.spinner) {
        clickShowSpinner.value = true;
    }

    await callClickHandler(evt);

    if (props.spinner) {
        clickShowSpinner.value = false;
    }
};

const handleClick = async (evt: MouseEvent): Promise<void> => {
    if (disabled.value) {
        evt.preventDefault();
        evt.stopImmediatePropagation();

        return;
    }

    await runClickHandler(evt);
};

const handleRouterClick = async (evt: MouseEvent, navigate: (event?: MouseEvent) => void | Promise<void>): Promise<void> => {
    if (disabled.value) {
        evt.preventDefault();
        evt.stopImmediatePropagation();

        return;
    }

    await runClickHandler(evt);

    if (!evt.defaultPrevented) {
        await navigate(evt);
    }
};
</script>
